import { useCallback, useRef, useState } from 'react';

interface RovingItem {
  /** Disabled items are kept in the DOM order but skipped by arrow keys + focus. */
  disabled?: boolean;
}

interface RovingRowProps {
  ref: (el: HTMLElement | null) => void;
  tabIndex: number;
  onKeyDown: React.KeyboardEventHandler<HTMLElement>;
}

/**
 * Roving-tabindex for a single-select vertical list (one Tab stop; Arrow
 * Up/Down, Home/End move focus between rows). Exactly one enabled row is
 * focusable at a time; disabled rows are skipped. Activation (Enter/Space) is
 * left to the row's native `button` semantics, so callers only wire selection
 * via `onSelect`.
 *
 * Returns `getRowProps(index)` to spread onto each row. The active index seeds
 * from the first enabled row and follows arrow navigation; it is presentational
 * focus only and intentionally decoupled from which row is *selected* (that is
 * URL state owned by the rail).
 */
export const useRovingTabIndex = (items: RovingItem[]) => {
  const firstEnabled = items.findIndex((it) => !it.disabled);
  const [activeIndex, setActiveIndex] = useState(firstEnabled === -1 ? 0 : firstEnabled);
  const refs = useRef<(HTMLElement | null)[]>([]);

  const focusIndex = useCallback((index: number) => {
    setActiveIndex(index);
    refs.current[index]?.focus();
  }, []);

  const step = useCallback(
    (from: number, dir: 1 | -1) => {
      const count = items.length;
      for (let i = 1; i <= count; i += 1) {
        const next = (from + dir * i + count) % count;
        if (!items[next]?.disabled) return next;
      }
      return from;
    },
    [items]
  );

  const edge = useCallback(
    (dir: 1 | -1) => {
      const order = dir === 1 ? items.map((_, i) => i) : items.map((_, i) => items.length - 1 - i);
      return order.find((i) => !items[i]?.disabled) ?? 0;
    },
    [items]
  );

  const getRowProps = useCallback(
    (index: number): RovingRowProps => ({
      ref: (el) => {
        refs.current[index] = el;
      },
      tabIndex: index === activeIndex ? 0 : -1,
      onKeyDown: (e) => {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            focusIndex(step(index, 1));
            break;
          case 'ArrowUp':
            e.preventDefault();
            focusIndex(step(index, -1));
            break;
          case 'Home':
            e.preventDefault();
            focusIndex(edge(1));
            break;
          case 'End':
            e.preventDefault();
            focusIndex(edge(-1));
            break;
          default:
            break;
        }
      },
    }),
    [activeIndex, focusIndex, step, edge]
  );

  return { getRowProps };
};
